import React, { useCallback, useEffect, useRef } from 'react'
import ApexCharts from 'apexcharts'
import { useThemeMode } from '../../../_metronic/partials'
import { getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import {
  montarOpcoesBarrasEmpilhadas,
  percentuaisRealizacao,
  PontoRealizacao,
} from './barrasEmpilhadasOpcoes'
import { rotuloDoPonto } from './serieDiariaOpcoes'

type Props = {
  pontos: PontoRealizacao[]
  nomes: { realizados: string; cancelados: string; faltas: string }
  formatarPercentual: (valor: number) => string
  formatarEixoY: (valor: number) => string
  formatarRotuloDia: (data: string) => string
  formatarIntervalo?: (dataInicio: string, dataFim: string) => string
  textoSemDados: string
  tabela: { legenda: string; colunaData: string; colunaN: string }
}

/** Barras empilhadas de realizados, cancelados e faltas em % dos lançados; ponto sem dado fica vazio. */
const BarrasEmpilhadasChart: React.FC<Props> = ({
  pontos,
  nomes,
  formatarPercentual,
  formatarEixoY,
  formatarRotuloDia,
  formatarIntervalo,
  textoSemDados,
  tabela,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()
  const temDado = pontos.some((ponto) => ponto.realizados !== null && ponto.n > 0)

  const refreshChart = useCallback(() => {
    if (!chartRef.current || !temDado) return

    const chart = new ApexCharts(
      chartRef.current,
      montarOpcoesBarrasEmpilhadas({
        pontos,
        nomes,
        cores: {
          realizados: getCSSVariableValue('--bs-gray-300'),
          cancelados: getCSSVariableValue('--app-chart-series'),
          faltas: getCSSVariableValue('--app-chart-series-alt'),
          texto: getCSSVariableValue('--bs-gray-700'),
          grade: getCSSVariableValue('--bs-gray-300'),
          superficie: getCSSVariableValue('--app-surface-bg'),
          semDado: getCSSVariableValue('--bs-gray-700'),
        },
        formatarPercentual,
        formatarEixoY,
        formatarRotuloDia,
        formatarIntervalo,
        textoSemDados,
        larguraGrafico: chartRef.current.clientWidth || undefined,
      })
    )

    chart.render()

    return chart
  }, [
    pontos,
    temDado,
    nomes,
    formatarPercentual,
    formatarEixoY,
    formatarRotuloDia,
    formatarIntervalo,
    textoSemDados,
  ])

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [mode, refreshChart])

  if (!temDado) {
    return (
      <p className='d-flex align-items-center text-gray-700 fs-6 mb-0 py-10 justify-content-center'>
        <i className='bi bi-dash-circle me-2 fs-5' aria-hidden='true' />
        <span>{textoSemDados}</span>
      </p>
    )
  }

  const celula = (valor: number | null) =>
    valor === null ? textoSemDados : formatarPercentual(valor)

  return (
    <>
      <div ref={chartRef} aria-hidden='true' />
      {/* O `visually-hidden` vai no wrapper: a tabela não encolhe abaixo do conteúdo e alargaria a página. */}
      <div className='visually-hidden'>
        <table>
          <caption>{tabela.legenda}</caption>
          <thead>
            <tr>
              <th scope='col'>{tabela.colunaData}</th>
              <th scope='col'>{nomes.realizados}</th>
              <th scope='col'>{nomes.cancelados}</th>
              <th scope='col'>{nomes.faltas}</th>
              <th scope='col'>{tabela.colunaN}</th>
            </tr>
          </thead>
          <tbody>
            {pontos.map((ponto) => {
              const p = percentuaisRealizacao(ponto)
              return (
                <tr key={ponto.data}>
                  <td>{rotuloDoPonto(ponto, formatarRotuloDia, formatarIntervalo)}</td>
                  <td>{celula(p.realizados)}</td>
                  <td>{celula(p.cancelados)}</td>
                  <td>{celula(p.faltas)}</td>
                  <td>{ponto.n}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}

export { BarrasEmpilhadasChart }
